/*------------------------------------------------------------------------------
   About      : QA-status control driven by `allowedTransitions`
                (epic DA-697, II-FE-5)

                **Options come from the record's `allowedTransitions`, never
                from CONTENT_VERIFICATION_STATUS.** The allowed set is per-record
                because of the cross-entity gate against tagged recommendations
                (DA-2372), so it cannot be derived from the enum or from the
                workflow table alone.

                A blocked edge arrives WITH a `blockedReason` rather than being
                omitted, specifically so the UI can grey it out and explain
                instead of silently hiding a transition the user expects to see.
                The reason is rendered verbatim: the gate is a monotonic floor
                ("has reached", not "is in"), because publishing a
                recommendation resets its status to TODO — so recomputing it
                client-side would strand insights through no user action.

                Display text comes from the workflow table's `statusLabels`
                (`READY_FOR_QA` → "Ready for review"), never a hardcoded map:
                CONTENT_VERIFICATION_STATUS is shared with recommendations and
                surveys and cannot be renamed for insights alone.

   Copyright (c) 2026 Bidgely. All rights reserved.
------------------------------------------------------------------------------*/
import { Box, MenuItem, TextField, Tooltip, Typography } from '@mui/material'
import BlockIcon from '@mui/icons-material/Block'

import { CONTENT_VERIFICATION_STATUS } from 'types/cms/frontendTypes'
import { AllowedTransition }           from '../types/insightTypes'

interface QaStatusSelectProps {
  current      : CONTENT_VERIFICATION_STATUS
  transitions  : AllowedTransition[]
  statusLabels : Partial<Record<CONTENT_VERIFICATION_STATUS, string>>
  disabled    ?: boolean
  onChange     : (next: CONTENT_VERIFICATION_STATUS) => void
  /** Called when a blocked option is chosen, for analytics. */
  onBlocked   ?: (transition: AllowedTransition) => void
  size        ?: 'small' | 'medium'
  /** Narrowed in the list table, where the column has to earn its width. The
   *  longest label ("Ready to publish") still fits at 150. */
  minWidth    ?: number
}

export const QaStatusSelect = ({
  current,
  transitions,
  statusLabels,
  disabled,
  onChange,
  onBlocked,
  size = 'small',
  minWidth = 180,
}: QaStatusSelectProps) => {

  const labelFor = (status: CONTENT_VERIFICATION_STATUS) => statusLabels[status] ?? status

  /* The current status is always present as an option even if the server did
     not list it as a transition-to-self, or MUI renders an empty select and the
     user cannot see where the record actually is. */
  const options : AllowedTransition[] = transitions.some((t) => t.to === current)
    ? transitions
    : [{ to : current }, ...transitions]

  return (
    <TextField
      select
      size={size}
      value={current}
      disabled={disabled}
      onChange={(event) => {
        const next       = event.target.value as CONTENT_VERIFICATION_STATUS
        const transition = options.find((t) => t.to === next)

        // Belt and braces: MenuItem is already disabled, but a keyboard
        // selection or a programmatic change must not slip a blocked
        // transition through to the server.
        if (transition?.blockedReason) {
          onBlocked?.(transition)
          return
        }
        if (next !== current) onChange(next)
      }}
      sx={{ minWidth }}
    >
      {options.map((transition) => {
        const blocked = !!transition.blockedReason

        const item = (
          <MenuItem
            key={transition.to}
            value={transition.to}
            disabled={blocked}
            sx={blocked ? { opacity : 0.6 } : undefined}
          >
            <Box sx={{ display : 'flex', alignItems : 'center', gap : 0.75, minWidth : 0 }}>
              {blocked && <BlockIcon fontSize="inherit" color="disabled" />}
              <Typography variant="body2" noWrap>{labelFor(transition.to)}</Typography>
            </Box>
          </MenuItem>
        )

        // Tooltip needs a non-disabled child to receive pointer events, so it
        // wraps a span rather than the MenuItem itself.
        return blocked ? (
          <Tooltip key={transition.to} title={transition.blockedReason!} placement="left" arrow>
            <span>{item}</span>
          </Tooltip>
        ) : item
      })}
    </TextField>
  )
}

export default QaStatusSelect
